/**
 * Graphical requests API documentation — the language of the text and the
 * rendering of the {{...}} tokens of the sections (code samples with one tab
 * per client language, the output format table, the API base URL).
 *
 * The documentation exists in en, fr, nl and de; the other dashboard
 * languages read it in English. The reader may switch the language of the
 * documentation (kept in this browser) without changing the dashboard's.
 */
define('apiDocsContent',[
  'underscore',
  'jquery',
  'settings',
  'apiDocsContentEn',
  'apiDocsContentFr',
  'apiDocsContentNl',
  'apiDocsContentDe',
  'apiDocsSamples'
], function (_, $, Settings, En, Fr, Nl, De, Samples) {
  'use strict';

  var BY_CODE = {en: En, fr: Fr, nl: Nl, de: De},
    LANGUAGE_KEY = 'apiDocs.language',
    CLIENT_KEY = 'apiDocs.client';

  // Same list as GraphicalRequestApiCatalog.OUTPUT_FORMATS in the backend.
  var FORMATS = [
    ['ai_cs2', '.ai (Adobe Illustrator CS2)'], ['ai_cs4', '.ai (Adobe Illustrator CS4)'],
    ['ai_cs5', '.ai (Adobe Illustrator CS5)'], ['ai_cs6', '.ai (Adobe Illustrator CS6)'],
    ['ai_cc', '.ai (Adobe Illustrator CC)'], ['pdf_1_4', '.pdf (PDF 1.4)'], ['pdf_1_5', '.pdf (PDF 1.5)'],
    ['pdf_1_6', '.pdf (PDF 1.6)'], ['pdf_1_7', '.pdf (PDF 1.7)'], ['psd', '.psd (Photoshop Document)'],
    ['cdr_x3', '.cdr (Corel Draw X3)'], ['cdr_x4', '.cdr (Corel Draw X4)'], ['cdr_x5', '.cdr (Corel Draw X5)'],
    ['cdr_x6', '.cdr (Corel Draw X6)'], ['cdr_x7', '.cdr (Corel Draw X7)'], ['indd_cs5', '.indd (Adobe Indesign CS5)'],
    ['indd_cs6', '.indd (Adobe Indesign CS6)'], ['indd_cc', '.indd (Adobe Indesign CC)'],
    ['eps_cs4', '.eps (Encapsulated Post Script Illustrator CS4)'],
    ['eps_cs5', '.eps (Encapsulated Post Script Illustrator CS5)'],
    ['eps_cs6', '.eps (Encapsulated Post Script Illustrator CS6)'],
    ['eps_cc', '.eps (Encapsulated Post Script Illustrator CC)'],
    ['svg_1_1', '.svg (Scalable Vector Graphics 1.1)'], ['svg_1_2', '.svg (Scalable Vector Graphics 1.2)'],
    ['bmp', '.bmp'], ['gif', '.gif'], ['jpg', '.jpg'], ['png', '.png'], ['tiff', '.tiff']
  ];

  function read(key) {
    try {
      return window.localStorage ? window.localStorage.getItem(key) : null;
    } catch (e) {
      return null;
    }
  }

  function write(key, value) {
    try {
      if (window.localStorage) {
        window.localStorage.setItem(key, value);
      }
    } catch (e) {
      // private window / blocked storage: the choice just is not remembered
    }
  }

  function escape(text) {
    return _.escape(text);
  }

  function sampleHtml(name, baseUrl, ui, client) {
    var sample = Samples[name];
    if (!sample) {
      return '';
    }
    if (sample.json) {
      return '<div class="api-doc-code"><div class="api-doc-code-bar"><span class="api-doc-code-label">JSON</span>' +
        '<button type="button" class="api-doc-copy js-copy">' + escape(ui.copy) + '</button></div>' +
        '<pre><code>' + escape(sample.json.split('{{BASE}}').join(baseUrl)) + '</code></pre></div>';
    }
    var clients = _.filter(Samples.clients, function (c) { return !!sample[c.id]; }),
      current = _.findWhere(clients, {id: client}) || clients[0],
      tabs = _.map(clients, function (c) {
        return '<button type="button" class="api-doc-code-tab js-client' + (c === current ? ' on' : '') +
          '" data-client="' + c.id + '">' + escape(c.label) + '</button>';
      }).join(''),
      panes = _.map(clients, function (c) {
        return '<pre class="api-doc-code-pane" data-client="' + c.id + '"' + (c === current ? '' : ' style="display:none"') +
          '><code>' + escape(sample[c.id].split('{{BASE}}').join(baseUrl)) + '</code></pre>';
      }).join('');
    return '<div class="api-doc-code"><div class="api-doc-code-bar">' + tabs +
      '<button type="button" class="api-doc-copy js-copy">' + escape(ui.copy) + '</button></div>' + panes + '</div>';
  }

  function formatsHtml() {
    return '<table class="api-doc-table api-doc-formats"><thead><tr><th>outputFormat</th><th>outputFormatLabel</th></tr></thead><tbody>' +
      _.map(FORMATS, function (f) {
        return '<tr><td><code>' + f[0] + '</code></td><td>' + escape(f[1]) + '</td></tr>';
      }).join('') + '</tbody></table>';
  }

  return {
    languages: [
      {code: 'en', label: 'English'},
      {code: 'fr', label: 'Français'},
      {code: 'nl', label: 'Nederlands'},
      {code: 'de', label: 'Deutsch'}
    ],

    /** The reader's choice, else the dashboard language when translated, else English. */
    language: function () {
      var chosen = read(LANGUAGE_KEY),
        dashboard = (Settings.get('lang') || 'en').substring(0, 2).toLowerCase();
      if (chosen && BY_CODE[chosen]) {
        return chosen;
      }
      return BY_CODE[dashboard] ? dashboard : 'en';
    },

    setLanguage: function (code) {
      if (BY_CODE[code]) {
        write(LANGUAGE_KEY, code);
      }
    },

    client: function () {
      return read(CLIENT_KEY) || 'curl';
    },

    setClient: function (id) {
      write(CLIENT_KEY, id);
    },

    get: function (code) {
      return BY_CODE[code] || En;
    },

    /** Copies text and says so on the button for a moment. */
    copy: function (text, $button, ui) {
      var done = function () {
        $button.text(ui.copied).addClass('done');
        setTimeout(function () { $button.text(ui.copy).removeClass('done'); }, 1500);
      };
      var fallback = function () {
        var $area = $('<textarea readonly></textarea>').val(text).css({position: 'fixed', left: '-9999px'}).appendTo('body');
        $area[0].select();
        try {
          document.execCommand('copy');
        } catch (e) {
          // nothing more can be done
        }
        $area.remove();
        done();
      };
      if (navigator.clipboard && navigator.clipboard.writeText) {
        navigator.clipboard.writeText(text).then(done, fallback);
      } else {
        fallback();
      }
    },

    /** The HTML of a section body, tokens replaced. */
    render: function (body, baseUrl, ui, client) {
      return body
        .replace(/\{\{sample:([a-zA-Z]+)\}\}/g, function (m, name) { return sampleHtml(name, baseUrl, ui, client); })
        .replace(/\{\{formats\}\}/g, formatsHtml())
        .split('{{BASE}}').join(escape(baseUrl));
    }
  };
});

